<template>
  <el-tag
    :key="text"
    :disable-transitions="true"
    :type="elTagType"
    :size="tagSize"
    :class="[
      `dd-tag--${type}`,
      {'el-tag--empty': type !== 'label'},
      {'dd-tag--selected': isSelected},
      {'dd-tag--border-radius': hasBorderRadius},
    ]"
    :style="styleClass"
    class="dd-tag"
  >
    <!-- Tag type input -->
    <template v-if="type==='input'">
      <div class="dd-tag--input__content">
        <div class="dd-tag--input__content-text">{{ text }}</div>
      </div>
      <i v-if="closable" class="ddi ddi-close dd-tag--input__icon" @click="closeTag" />
    </template>
    <slot />
  </el-tag>
</template>

<script lang="ts">
const colorMap: any = {
  'reddish-orange': '#fa6123',
  'algae-green': '#21b963',
  'dodger-blue': '#3294f1',
  'light-orange': '#faa33e',
  'bluey-purple': '#5d40c6',
  'darkish-pink': '#e14f9d',
  'dark-teal': '#01363d',
  'bluish-grey': '#778c90',
  'light-grey-three': '#e7e4df',
  'off-white': '#faf9f6',
  kabul: '#604c3f',
  strawberry: '#ff2629',
  viridian: '#198c62',
  slate: '#3f5b60',
}

import { defineComponent } from 'vue'

/**
 * DdTag component is based on ElTag to display a tag.
 */

export default defineComponent({
  name: 'DdTag',

  props: {
    /**
     * Tag description text - for tag input
     */
    text: {
      type: String,
      default: '',
    },
    /**
     * Boolean indicating if DdTag is selected
     */
    isSelected: {
      type: Boolean,
      default: false,
    },
    /**
     * Type of tag: filled, outline, input, label
     */
    type: {
      type: String,
      default: 'input',
    },
    /**
     * Color of tag
     */
    color: {
      type: String,
      default: 'info',
    },

    /**
     * Determines if remove icon should or not be rendered
     */
    closable: {
      type: Boolean,
      default: true,
    },

    /**
     * Determines if tag has border radius
     */
    hasBorderRadius: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['close'],

  computed: {
    /* Element's tag colors only apply to labels; other tags are styled here */
    elTagType(): 'primary' | 'success' | 'warning' | 'info' | 'danger' | undefined {
      const elementTypes = ['primary', 'success', 'warning', 'info', 'danger'] as const
      const color = elementTypes.find(type => type === this.color)
      return this.type === 'label' ? color : undefined
    },
    /* Element UI's small, medium and mini tags were 24px, 28px and 20px tall */
    tagSize(): 'default' | 'small' {
      if (this.type === 'label' || this.type === 'input') {
        return 'default'
      } else {
        return 'small'
      }
    },
    styleClass(): Record<string, any> {
      if (this.type === 'filled') {
        return this.filledClass
      } else if (this.type === 'outline') {
        return this.outlineClass
      } else {
        return {}
      }
    },
    outlineClass(): Record<string, any> {
      return {
        padding: '3px 6px',
        lineHeight: 1,
        color:
          this.color !== 'info'
            ? colorMap[this.color]
            : colorMap['bluish-grey'],
        textTransform: 'uppercase',
        backgroundColor: '#fff',
        border: `1px solid #dfe4e5`,
      }
    },
    filledClass(): Record<string, any> {
      return {
        padding: '0 8px',
        lineHeight: '18px',
        color: '#fff',
        backgroundColor:
          this.color !== 'info'
            ? colorMap[this.color]
            : colorMap['bluish-grey'],
      }
    },
  },

  methods: {
    closeTag(e: any) {
      this.$emit('close', e)
    },
  },
})
</script>

<style lang="scss" scoped>
.dd-tag {
  &.el-tag {
    &::before {
      display: inline-block;
      width: 3px;
      height: calc(100% - 4px);
      margin-right: 6px;
      vertical-align: text-top;
      content: '';
      border-radius: 3px;
      opacity: 0.56;
    }

    &--empty {
      border: 1px solid $light-blue-grey-two;
      &::before {
        display: none;
      }
    }

    &--info {
      &::before {
        background-color: var(--color-text-third);
      }
    }

    &--warning {
      &::before {
        background-color: $reddish-orange;
      }
    }

    &--small {
      padding: 2px 12px;
      padding-left: 4px;
      line-height: 1.54;
    }

    &--notask {
      padding-left: 12px;
      color: $color--gray-60 !important;
    }

    &--mini {
      height: 18px;
      font-size: 11px;
      line-height: 10px;
      vertical-align: middle;
    }

    &--bold {
      font-weight: 500;
    }

    &--italic {
      font-style: italic;
    }
  }

  &--selected {
    border: solid 1px $color--blue !important;
  }

  &--border-radius {
    background-color: rgba(1, 54, 61, 0.08);
    border: 0 !important;
    border-radius: 14px !important;

    &:hover {
      background-color: rgba(1, 54, 61, 0.16);
      background-image: none;
    }

    &.dd-tag--input {
      background-image: none;

      &__content {
        padding: 0 4px 0 12px;
      }
    }
  }

  &--input {
    display: flex;
    align-items: center;
    /* Element UI's medium tag height, which Element Plus doesn't have */
    height: 28px;
    padding: 4px;
    background-color: var(--color-background-secondary);
    border: 1px solid $light-blue-grey-two;
    border-radius: 4px;
    @include lettering--p-sm;

    /* Element Plus wraps the tag's content in a span */
    :deep(.el-tag__content) {
      display: flex;
      align-items: center;
    }

    &__content {
      display: flex;
      align-items: center;

      &-text {
        padding: 0 4px;
      }

      .avatar {
        margin-left: -1px;
      }
    }

    &__icon.ddi {
      position: initial;
      margin: 0 4px;
      cursor: pointer;
      opacity: 0.5;

      &:hover {
        opacity: 1 !important;
      }
    }
  }

  &--filled {
    border: none !important;
  }

  &--filled,
  &--outline {
    position: relative;
    width: fit-content;
    height: 18px;
    font-size: 11px;
    font-weight: 500;
    cursor: default;
    border-radius: 4px;
  }

  .ddi-close {
    font-size: 10px;
    @include clickable-gray-color-states;
  }
}
</style>
